<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useTranslation } from "i18next-vue";
import { Facade } from "@/drawing";
import type { ObservationEntityRef } from "@/drawing/managers/ObservationsManager";
import { useGetDiagnosticImages } from "@/stores/queries/diagnostic-images.query";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRDialog from "@/components/UI/feedbacks/DRDialog.vue";
import { Z_INDEX_DRAWER } from "@/components/canvas/layouts/constants";
import {
  canvasTopForTexts,
  rightOverlayClearancePx,
} from "@/stores/CanvasLayoutState";
import { getCanvasTextScale } from "@/composable/useCanvasUiScale";

/**
 * Popup persistante (non modale, téléportée hors du volet) listant tous les
 * marqueurs photo du plan. Deux étapes : 1er clic = zoom sur le marqueur,
 * 2e clic = action (rattacher à l'observation en mode `attach`, créer une
 * observation en mode `browse`). Les marqueurs dont le numéro a déjà une
 * photo associée (galerie) sont désactivés.
 */

const props = defineProps<{
  diagnosticId: number;
  /** attach = rattacher à l'observation `attachLocalId` ; browse = explorer/créer. */
  mode: "attach" | "browse";
  attachLocalId?: string;
  entitiesByObservation: Map<string, ObservationEntityRef[]>;
}>();

const emit = defineEmits<{
  (e: "close"): void;
  (e: "stamp", handles: string[], observationLocalId: string): void;
  (e: "create", handle: string): void;
}>();

const { t } = useTranslation();

interface MarkerRow {
  handle: string;
  number: number | null;
  label: string;
  /** localId de l'observation à laquelle le marqueur est déjà rattaché. */
  attachedTo: string | null;
}

const markers = ref<MarkerRow[]>([]);

/** Index inverse handle → localId d'observation. */
const attachedByHandle = computed(() => {
  const map = new Map<string, string>();
  for (const [obsId, refs] of props.entitiesByObservation) {
    for (const entityRef of refs) map.set(entityRef.handle, obsId);
  }
  return map;
});

/**
 * Un libellé exploitable est un nombre : le rattachement photo↔marqueur passe
 * par `DiagnosticImage.photoNumber`, un entier. Un marqueur horodaté
 * (« 16:47:48 ») serait réduit à « 16 » — on le signale au lieu de mentir.
 */
const isNumericLabel = (label: string): boolean => /^\s*\d+\s*$/.test(label);

const showTimestampInfo = ref(false);

const loadMarkers = () => {
  if (!Facade.isInitialized) return;
  markers.value = Facade.activity
    .listEntities()
    .filter((e) => e.kind === "photoMarker")
    .map((e) => {
      const match = (e.markerLabel ?? "").match(/\d+/);
      return {
        handle: e.handle,
        number: match ? parseInt(match[0], 10) : null,
        label: e.markerLabel ?? "?",
        attachedTo: attachedByHandle.value.get(e.handle) ?? null,
      };
    });
};

// Le dessin peut évoluer pendant que la popup est ouverte (pose d'un nouveau
// marqueur) — même pattern débouncé que le fil Activité.
let historyHandler: (() => void) | null = null;
let reloadTimer: ReturnType<typeof setTimeout> | null = null;
onMounted(() => {
  loadMarkers();
  if (!Facade.isInitialized) return;
  historyHandler = (): void => {
    if (reloadTimer) clearTimeout(reloadTimer);
    reloadTimer = setTimeout(loadMarkers, 300);
  };
  Facade.history.onChange(historyHandler);
});
onUnmounted(() => {
  if (historyHandler && Facade.isInitialized) {
    Facade.history.offChange(historyHandler);
  }
  if (reloadTimer) clearTimeout(reloadTimer);
});

// Numéros déjà couverts par une photo de la galerie → marqueur désactivé.
const { data: galleryImages } = useGetDiagnosticImages(
  computed(() => props.diagnosticId || undefined),
);
const coveredNumbers = computed(() => {
  const set = new Set<number>();
  for (const img of galleryImages.value ?? []) {
    if (img.photoNumber != null) set.add(img.photoNumber);
  }
  return set;
});

const isDisabled = (m: MarkerRow): boolean =>
  m.number != null && coveredNumbers.value.has(m.number);

const sortedMarkers = computed(() =>
  [...markers.value].sort((a, b) => (a.number ?? 1e9) - (b.number ?? 1e9)),
);

// ── Deux étapes : zoom puis action ──────────────────────────────────────────
const armedHandle = ref<string | null>(null);

const onMarkerClick = (m: MarkerRow) => {
  if (isDisabled(m)) return;
  if (armedHandle.value === m.handle) {
    // 2e clic : action
    if (props.mode === "attach" && props.attachLocalId) {
      emit("stamp", [m.handle], props.attachLocalId);
    } else {
      emit("create", m.handle);
    }
    armedHandle.value = null;
    return;
  }
  // 1er clic : zoom + sélection du marqueur (la bbox se recale dessus ; un
  // simple zoom laisserait la grille de la sélection précédente en place).
  armedHandle.value = m.handle;
  if (Facade.isInitialized) {
    Facade.selection.highlightByHandles([m.handle]);
  }
};
</script>

<template>
  <Teleport to="body">
    <div
      class="marker-picker shadow-3 rounded d-flex flex-column canvas-text-scaled"
      :style="{
        top: `${canvasTopForTexts + 12}px`,
        // À gauche du volet et de ses onglets en bulles.
        right: `${rightOverlayClearancePx / getCanvasTextScale() + 4}px`,
        zIndex: Z_INDEX_DRAWER + 1,
      }"
    >
      <div class="d-flex align-items-center gap-2 p-2">
        <PhosphorIcon name="map-pin" :size="16" weight="bold" />
        <span class="fw-medium flex-grow-1">
          {{ t("canvas:observations.markerPicker.title") }}
        </span>
        <DRBtnIcon
          variant="flat-white"
          :size="24"
          phosphor-icon="x"
          @click="emit('close')"
        />
      </div>

      <div class="hint px-2 pb-1">
        {{
          mode === "attach"
            ? t("canvas:observations.markerPicker.hintAttach")
            : t("canvas:observations.markerPicker.hintBrowse")
        }}
      </div>

      <div class="marker-list flex-grow-1 overflow-auto p-2 pt-1">
        <div v-if="!sortedMarkers.length" class="hint text-center py-3">
          {{ t("canvas:observations.markerPicker.empty") }}
        </div>

        <button
          v-for="m in sortedMarkers"
          :key="m.handle"
          class="marker-row w-100 d-flex align-items-center gap-2 rounded"
          :class="{ armed: armedHandle === m.handle, disabled: isDisabled(m) }"
          :disabled="isDisabled(m)"
          @click="onMarkerClick(m)"
        >
          <span class="marker-number flex-shrink-0">{{ m.label }}</span>
          <span
            v-if="!isNumericLabel(m.label)"
            class="timestamp-badge flex-shrink-0"
            role="button"
            :title="t('canvas:observations.markerPicker.timestampBadge')"
            @click.stop.prevent="showTimestampInfo = true"
          >
            <PhosphorIcon name="warning" :size="11" weight="fill" />
          </span>
          <span class="flex-grow-1 text-start marker-state">
            <template v-if="isDisabled(m)">
              {{ t("canvas:observations.markerPicker.covered") }}
            </template>
            <template v-else-if="armedHandle === m.handle">
              {{
                mode === "attach"
                  ? t("canvas:observations.markerPicker.clickToAttach")
                  : t("canvas:observations.markerPicker.clickToCreate")
              }}
            </template>
            <template v-else-if="m.attachedTo">
              {{ t("canvas:observations.markerPicker.attached") }}
            </template>
          </span>
          <PhosphorIcon
            v-if="isDisabled(m)"
            name="check"
            :size="14"
            weight="bold"
            class="flex-shrink-0"
          />
          <PhosphorIcon
            v-else
            :name="
              armedHandle === m.handle ? 'plus-circle' : 'crosshair-simple'
            "
            :size="14"
            weight="bold"
            class="flex-shrink-0"
          />
        </button>
      </div>
    </div>
  </Teleport>

  <DRDialog
    v-model="showTimestampInfo"
    class="canvas-dialog"
    :title="t('canvas:observations.markerPicker.timestampTitle')"
    hide-footer
    :width="420"
  >
    <p class="mb-0" style="white-space: pre-line; line-height: 1.5">
      {{ t("canvas:observations.markerPicker.timestampMessage") }}
    </p>
  </DRDialog>
</template>

<style lang="scss" scoped>
.marker-picker {
  position: fixed;
  width: 230px;
  max-height: 60vh;
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  color: white;
}

.timestamp-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: var(--bs-danger);
  color: #fff;
}

.hint {
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.6);
}

.marker-row {
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.85);
  padding: 5px 6px;
  font-size: 0.78rem;

  &:hover:not(.disabled) {
    background-color: rgba(255, 255, 255, 0.08);
  }

  &.armed {
    background-color: color-mix(in srgb, var(--bs-primary) 25%, transparent);
    color: white;
  }

  &.disabled {
    color: rgba(255, 255, 255, 0.35);
    cursor: default;
  }
}

.marker-number {
  min-width: 30px;
  text-align: center;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 1px 6px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.marker-state {
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.55);

  .armed & {
    color: white;
  }
}
</style>
